import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import {
	getInstallHintMode,
	INSTALL_HINT_DISMISSED_KEY,
} from '#app/utils/install-hint.ts'
import { Button } from './ui/button.tsx'
import { Icon } from './ui/icon.tsx'

type InstallPromptEvent = Event & {
	prompt: () => Promise<void>
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function isStandalone() {
	return (
		window.matchMedia('(display-mode: standalone)').matches ||
		(navigator as Navigator & { standalone?: boolean }).standalone === true
	)
}

function readDismissed() {
	try {
		return localStorage.getItem(INSTALL_HINT_DISMISSED_KEY) === 'true'
	} catch {
		return false
	}
}

/** Dismissible hint to add the app to the home screen, for signed-in users. */
export function InstallHint() {
	const { pathname } = useLocation()
	const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(
		null,
	)
	const [device, setDevice] = useState<{
		userAgent: string
		maxTouchPoints: number
		standalone: boolean
		dismissed: boolean
	} | null>(null)

	useEffect(() => {
		setDevice({
			userAgent: navigator.userAgent,
			maxTouchPoints: navigator.maxTouchPoints,
			standalone: isStandalone(),
			dismissed: readDismissed(),
		})
		function onBeforeInstallPrompt(event: Event) {
			event.preventDefault()
			setPromptEvent(event as InstallPromptEvent)
		}
		function onInstalled() {
			setPromptEvent(null)
			setDevice((current) => current && { ...current, standalone: true })
		}
		window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
		window.addEventListener('appinstalled', onInstalled)
		return () => {
			window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
			window.removeEventListener('appinstalled', onInstalled)
		}
	}, [])

	const mode = device
		? getInstallHintMode({ ...device, canPrompt: promptEvent !== null })
		: null
	if (!mode || pathname === '/install') return null

	function dismiss() {
		try {
			localStorage.setItem(INSTALL_HINT_DISMISSED_KEY, 'true')
		} catch {
			// Storage blocked: the hint just returns next visit.
		}
		setDevice((current) => current && { ...current, dismissed: true })
	}

	async function install() {
		if (!promptEvent) return
		await promptEvent.prompt()
		await promptEvent.userChoice
		// The event can only be used once; the browser fires it again if eligible.
		setPromptEvent(null)
	}

	return (
		<div className="container mb-4">
			<aside
				aria-label="Install Youth Rollcall"
				className="bg-accent text-accent-foreground flex items-start gap-3 rounded-md border p-3 md:items-center"
			>
				<div className="flex-1 text-sm">
					{mode === 'prompt' ? (
						<p>Add Youth Rollcall to your home screen for quick access.</p>
					) : (
						<p>
							Add Youth Rollcall to your home screen: tap Share, then{' '}
							<strong>Add to Home Screen</strong>.
						</p>
					)}
					<Link
						to="/install"
						className="text-brand mt-1 inline-block underline underline-offset-4"
					>
						See the install guide
					</Link>
				</div>
				{mode === 'prompt' ? (
					<Button onClick={() => void install()}>Install app</Button>
				) : null}
				<Button
					variant="ghost"
					size="icon"
					className="-my-1 -mr-1 shrink-0"
					aria-label="Dismiss install hint"
					onClick={dismiss}
				>
					<Icon name="cross-1" />
				</Button>
			</aside>
		</div>
	)
}
